<template>
    <TprmModal
        open
        :title="title"
        additional-class-name="RevertActionModal"
        :show-close-icon="false"
        @close="$emit('close')"
    >
        <div class="RevertActionModal__body">
            <p v-if="checkingImpact" class="RevertActionModal__checking">
                Checking impact…
            </p>
            <p v-else-if="impactUnavailable" class="RevertActionModal__impactUnavailable">
                {{ impactUnavailableMessage }}
            </p>
            <template v-else>
                <p class="RevertActionModal__impact">
                    {{ impactMessage }}
                </p>
                <p v-if="affectedEntityCount === 0" class="RevertActionModal__zeroImpactExplanation">
                    {{ zeroImpactExplanation }}
                </p>
            </template>

            <div v-if="showNotificationWarning" class="RevertActionModal__notice">
                <EidIcon class="RevertActionModal__noticeIcon" :icon-src="faCircleExclamation" />
                <span class="RevertActionModal__notificationWarning">{{ notificationWarning }}</span>
            </div>

            <RbacDisplayGuard v-if="canPauseEntity" :features="[ pauseFeature ]">
                <div class="RevertActionModal__pause">
                    <Checkbox
                        v-model="pauseEntity"
                        class="RevertActionModal__pauseCheckbox"
                        aria-describedby="RevertActionModal__pauseHint"
                    >
                        {{ pauseLabel }}
                    </Checkbox>
                    <p id="RevertActionModal__pauseHint" class="RevertActionModal__pauseHint">
                        {{ pauseHint }}
                    </p>
                </div>
            </RbacDisplayGuard>

            <div class="RevertActionModal__reason">
                <label class="RevertActionModal__reasonLabel" for="RevertActionModal__reasonInput">
                    {{ reasonLabel }}
                </label>
                <textarea
                    id="RevertActionModal__reasonInput"
                    v-model="reason"
                    class="RevertActionModal__reasonInput"
                    maxlength="500"
                    rows="2"
                    placeholder="Why are you reverting this?"
                />
            </div>

            <Alert v-if="errorMessage" type="danger" class="RevertActionModal__errorAlert">
                <span class="RevertActionModal__error">{{ errorMessage }}</span>
            </Alert>
        </div>

        <template #footer>
            <div class="RevertActionModal__footer">
                <!-- While the request is in flight neither button is offered, so a 409 or a 403 arriving
                     mid-revert cannot be dismissed before it has been read. -->
                <template v-if="reverting">
                    <NewButton variant="fill" color="primary" disabled>
                        Reverting…
                    </NewButton>
                </template>
                <template v-else>
                    <NewButton variant="flat" color="neutral" @click="$emit('close')">
                        Cancel
                    </NewButton>
                    <NewButton
                        variant="fill"
                        color="primary"
                        :disabled="confirmDisabled"
                        @click="onConfirm"
                    >
                        Revert decision
                    </NewButton>
                </template>
            </div>
        </template>
    </TprmModal>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';

    import { Alert } from '@evidentid/dashboard-commons/components/Alert';
    import { Checkbox } from '@evidentid/dashboard-commons/components/Checkbox';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import RbacDisplayGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import {
        REVERT_IMPACT_UNAVAILABLE_MESSAGE,
        REVERT_NOTIFICATION_WARNING_ENTITY,
        REVERT_NOTIFICATION_WARNING_MANY,
        REVERT_PAUSE_HINT,
        REVERT_PAUSE_LABEL,
        REVERT_REQUEUE_SENTENCE,
        REVERT_ZERO_IMPACT_EXPLANATION,
        REVERT_ZERO_IMPACT_MESSAGE,
    } from '@/modules/entity-actions-review/constants';

    export default defineComponent({
        name: 'RevertActionModal',
        components: { Alert, Checkbox, EidIcon, NewButton, RbacDisplayGuard, TprmModal },
        props: {
            impactStatus: {
                type: String as PropType<OperationStatus>,
                default: OperationStatus.uninitialized,
            },
            affectedEntityCount: {
                type: Number as PropType<number>,
                default: 0,
            },
            errorMessage: {
                type: String as PropType<string | null>,
                default: null,
            },
            reverting: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            /* The entity this action belongs to, absent for a global action. A global revert can
               touch every entity assigned the criterion, so pausing one of them would suppress
               almost nothing -- the option is offered only where it does the whole job. */
            insuredId: {
                type: String as PropType<string | null>,
                default: null,
            },
            /* Names the entity in the title where the revert reaches only that one. Absent for a
               global action, and absent on a row that arrived without entityNames. */
            entityName: {
                type: String as PropType<string | null>,
                default: null,
            },
            /* Suppresses the outbound-email warning for a tenant whose notifications are already
               held, where the warning would be untrue. */
            notificationsPaused: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            /* Turns the reason into a precondition: the label says so and the confirm button stays
               disabled until something is typed. */
            reasonRequired: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'close', 'confirm' ],
        data() {
            return {
                reason: '',
                pauseEntity: false,
                pauseFeature: RbacFeature.dashboardPauseEntity,
                pauseLabel: REVERT_PAUSE_LABEL,
                pauseHint: REVERT_PAUSE_HINT,
                zeroImpactExplanation: REVERT_ZERO_IMPACT_EXPLANATION,
                impactUnavailableMessage: REVERT_IMPACT_UNAVAILABLE_MESSAGE,
                faCircleExclamation,
            };
        },
        computed: {
            /* Offered only when pausing this one entity suppresses the whole re-evaluation. A revert that
               reaches several entities would otherwise promise silence while every other affected entity
               still gets mail. `affectedEntityCount` is the number this dialog already shows, so the
               promise matches what the reviewer is reading. */
            canPauseEntity(): boolean {
                return this.insuredId != null && this.affectedEntityCount === 1;
            },
            /* The single-entity wording needs a name to put in the title; without one the dialog falls
               back to counting, which reads correctly either way. */
            singleEntity(): boolean {
                return this.canPauseEntity && Boolean(this.entityName);
            },
            title(): string {
                if (this.singleEntity) {
                    return `Revert decision for ${this.entityName}?`;
                }
                if (this.affectedEntityCount === 0) {
                    return 'Revert decision?';
                }
                const noun = this.affectedEntityCount === 1 ? 'entity' : 'entities';
                return `Revert decision for ${this.affectedEntityCount} ${noun}?`;
            },
            /* affectedEntityCount is the only signal this dialog has about how wide a revert reaches: the
               API's RevertImpact.scope token ('entity' | 'rpWide') never travels past the store, so these
               sentences are built from the count alone rather than from that token. */
            impactMessage(): string {
                if (this.affectedEntityCount === 0) {
                    return `${REVERT_ZERO_IMPACT_MESSAGE} ${REVERT_REQUEUE_SENTENCE}`;
                }
                if (this.singleEntity) {
                    return `This entity will be re-evaluated. ${REVERT_REQUEUE_SENTENCE}`;
                }
                const noun = this.affectedEntityCount === 1 ? 'entity' : 'entities';
                return `All ${this.affectedEntityCount} ${noun} will be re-evaluated. ${REVERT_REQUEUE_SENTENCE}`;
            },
            notificationWarning(): string {
                return this.singleEntity ? REVERT_NOTIFICATION_WARNING_ENTITY : REVERT_NOTIFICATION_WARNING_MANY;
            },
            /* The warning states that notifications are not held. Ticking the pause makes that untrue for
               the one entity in scope, and a tenant-wide hold makes it untrue outright, so in both cases
               the notice goes rather than contradicting itself. */
            showNotificationWarning(): boolean {
                return !this.notificationsPaused && !this.pauseEntity;
            },
            reasonLabel(): string {
                return this.reasonRequired ? 'Reason (required)' : 'Reason (optional)';
            },
            checkingImpact(): boolean {
                return this.impactStatus === OperationStatus.loading
                    || this.impactStatus === OperationStatus.uninitialized;
            },
            impactUnavailable(): boolean {
                return this.impactStatus === OperationStatus.error;
            },
            /* The impact count is the only guardrail on outbound email, so a revert cannot be confirmed
               until it is known, and confirming blind is refused outright if the impact check failed. */
            confirmDisabled(): boolean {
                return this.checkingImpact
                    || this.impactUnavailable
                    || this.reverting
                    || (this.reasonRequired && this.reason.trim() === '');
            },
        },
        methods: {
            onConfirm(): void {
                const trimmed = this.reason.trim();
                this.$emit('confirm', {
                    reason: trimmed === '' ? null : trimmed,
                    pauseEntity: this.pauseEntity,
                });
            },
        },
    });
</script>
